<script setup lang="ts">
import { computed } from 'vue'

interface Props {
  size?: 'sm' | 'md' | 'lg' | 'xl'
  state?: 'idle' | 'speaking' | 'listening' | 'thinking' | 'happy' | 'concerned'
  showBubble?: boolean
  bubbleContent?: string
}

const props = withDefaults(defineProps<Props>(), {
  size: 'md',
  state: 'idle',
  showBubble: false,
  bubbleContent: '',
})

const emit = defineEmits<{
  (e: 'click'): void
}>()

const sizeClasses = computed(() => {
  switch (props.size) {
    case 'sm': return 'w-12 h-12'
    case 'md': return 'w-16 h-16'
    case 'lg': return 'w-24 h-24'
    case 'xl': return 'w-32 h-32'
    default: return 'w-16 h-16'
  }
})

const avatarClass = computed(() => {
  const classes = ['sahbi-avatar']
  if (props.state === 'speaking') classes.push('speaking')
  if (props.state === 'listening') classes.push('listening')
  if (props.state === 'thinking') classes.push('thinking')
  if (props.state === 'happy') classes.push('happy')
  return classes.join(' ')
})

// Expression based on state
const expression = computed(() => {
  switch (props.state) {
    case 'happy': return { eyes: 'happy', mouth: 'smile' }
    case 'concerned': return { eyes: 'worried', mouth: 'frown' }
    case 'thinking': return { eyes: 'up', mouth: 'neutral' }
    case 'speaking': return { eyes: 'normal', mouth: 'open' }
    case 'listening': return { eyes: 'attentive', mouth: 'neutral' }
    default: return { eyes: 'normal', mouth: 'smile' }
  }
})

// Color based on glucose status (could be passed as prop)
const skinColor = '#FFD5B8'
const hairColor = '#4A3728'
const primaryColor = '#4CAF50'
</script>

<template>
  <div class="relative cursor-pointer" @click="emit('click')">
    <!-- Speech bubble -->
    <Transition
      enter-active-class="transition-all duration-200"
      enter-from-class="opacity-0 scale-75"
      enter-to-class="opacity-100 scale-100"
      leave-active-class="transition-all duration-200"
      leave-from-class="opacity-100 scale-100"
      leave-to-class="opacity-0 scale-75"
    >
      <div
        v-if="showBubble && bubbleContent"
        class="absolute -top-16 left-1/2 -translate-x-1/2 bg-white dark:bg-gray-800 rounded-xl px-4 py-2 shadow-lg max-w-[200px]"
      >
        <span class="text-2xl">{{ bubbleContent }}</span>
        <div class="absolute -bottom-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-white dark:bg-gray-800 rotate-45" />
      </div>
    </Transition>

    <!-- Avatar -->
    <svg
      :class="[sizeClasses, avatarClass]"
      viewBox="0 0 100 100"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <!-- Background circle -->
      <circle cx="50" cy="50" r="48" :fill="primaryColor" opacity="0.1" />
      <circle cx="50" cy="50" r="45" fill="white" />

      <!-- Face base -->
      <ellipse cx="50" cy="55" rx="30" ry="32" :fill="skinColor" />

      <!-- Hair -->
      <path
        d="M25 45 Q25 25 50 22 Q75 25 75 45 Q70 35 50 35 Q30 35 25 45Z"
        :fill="hairColor"
      />

      <!-- Eyes -->
      <g v-if="expression.eyes === 'normal'">
        <ellipse cx="38" cy="50" rx="5" ry="6" fill="white" />
        <ellipse cx="62" cy="50" rx="5" ry="6" fill="white" />
        <circle cx="38" cy="51" r="3" fill="#333" />
        <circle cx="62" cy="51" r="3" fill="#333" />
        <circle cx="39" cy="49" r="1" fill="white" />
        <circle cx="63" cy="49" r="1" fill="white" />
      </g>
      <g v-else-if="expression.eyes === 'happy'">
        <path d="M33 50 Q38 45 43 50" stroke="#333" stroke-width="2" fill="none" stroke-linecap="round" />
        <path d="M57 50 Q62 45 67 50" stroke="#333" stroke-width="2" fill="none" stroke-linecap="round" />
      </g>
      <g v-else-if="expression.eyes === 'worried'">
        <ellipse cx="38" cy="50" rx="5" ry="6" fill="white" />
        <ellipse cx="62" cy="50" rx="5" ry="6" fill="white" />
        <circle cx="38" cy="52" r="3" fill="#333" />
        <circle cx="62" cy="52" r="3" fill="#333" />
        <path d="M33 44 L43 46" stroke="#4A3728" stroke-width="2" stroke-linecap="round" />
        <path d="M67 44 L57 46" stroke="#4A3728" stroke-width="2" stroke-linecap="round" />
      </g>
      <g v-else-if="expression.eyes === 'up'">
        <ellipse cx="38" cy="50" rx="5" ry="6" fill="white" />
        <ellipse cx="62" cy="50" rx="5" ry="6" fill="white" />
        <circle cx="38" cy="48" r="3" fill="#333" />
        <circle cx="62" cy="48" r="3" fill="#333" />
      </g>
      <g v-else-if="expression.eyes === 'attentive'">
        <ellipse cx="38" cy="50" rx="6" ry="7" fill="white" />
        <ellipse cx="62" cy="50" rx="6" ry="7" fill="white" />
        <circle cx="38" cy="50" r="4" fill="#333" />
        <circle cx="62" cy="50" r="4" fill="#333" />
        <circle cx="40" cy="48" r="1.5" fill="white" />
        <circle cx="64" cy="48" r="1.5" fill="white" />
      </g>

      <!-- Nose -->
      <path d="M50 55 Q48 60 50 62 Q52 60 50 55" fill="#E8C4A8" />

      <!-- Mouth -->
      <g v-if="expression.mouth === 'smile'">
        <path d="M40 70 Q50 78 60 70" stroke="#C47B6B" stroke-width="2" fill="none" stroke-linecap="round" />
      </g>
      <g v-else-if="expression.mouth === 'open'">
        <ellipse cx="50" cy="72" rx="8" ry="5" fill="#C47B6B" />
        <ellipse cx="50" cy="71" rx="6" ry="3" fill="#8B4B4B" />
      </g>
      <g v-else-if="expression.mouth === 'frown'">
        <path d="M42 74 Q50 68 58 74" stroke="#C47B6B" stroke-width="2" fill="none" stroke-linecap="round" />
      </g>
      <g v-else>
        <line x1="43" y1="72" x2="57" y2="72" stroke="#C47B6B" stroke-width="2" stroke-linecap="round" />
      </g>

      <!-- Cheeks (blush) -->
      <circle cx="30" cy="60" r="5" fill="#FFB5B5" opacity="0.5" />
      <circle cx="70" cy="60" r="5" fill="#FFB5B5" opacity="0.5" />

      <!-- Status indicator -->
      <circle
        v-if="state === 'listening'"
        cx="85"
        cy="85"
        r="8"
        fill="#EF4444"
        class="animate-pulse"
      />
      <circle
        v-else-if="state === 'thinking'"
        cx="85"
        cy="85"
        r="8"
        fill="#F59E0B"
      >
        <animate attributeName="opacity" values="1;0.5;1" dur="1.5s" repeatCount="indefinite" />
      </circle>
      <circle
        v-else-if="state === 'speaking'"
        cx="85"
        cy="85"
        r="8"
        fill="#4CAF50"
      />
    </svg>

    <!-- Thinking dots animation -->
    <div
      v-if="state === 'thinking'"
      class="absolute -top-8 left-1/2 -translate-x-1/2 flex gap-1"
    >
      <span class="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style="animation-delay: 0s" />
      <span class="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style="animation-delay: 0.2s" />
      <span class="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style="animation-delay: 0.4s" />
    </div>
  </div>
</template>
